import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { getOpLabel, FAR_LINK_RECIPIENT, FAR_EXCLUSION, FAR_EXCLUSION_LABEL, ruleHasExclusions, describeExclusionCounts } from "../../../../assets/js/utils/far/farEngine";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";

const useStyles = makeStyles(theme => {
    const s = farShared(theme);
    const t = s.t;
    return {
        box: { borderRadius: "8px", border: "1px solid "+t.border, backgroundColor: t.dark ? "rgba(255,255,255,0.02)" : t.surfaceAlt, overflow: "hidden" },
        row: {
            display: "grid", gridTemplateColumns: "110px minmax(0, 1fr)", gap: "10px", padding: "9px 12px", fontSize: "13px", lineHeight: "18px",
            borderTop: "1px solid "+t.border, "&:first-child": { borderTop: "none" }
        },
        compactRow: { padding: "6px 10px", gridTemplateColumns: "100px minmax(0, 1fr)" },
        key: { color: t.textMuted, fontWeight: 600 },
        value: { color: t.text, wordBreak: "break-word", "& span": { display: "block" } },
        note: { color: t.textMuted, fontSize: "12px" },
        bad: { color: t.danger.fg, fontWeight: 600 },
        badges: { display: "flex", gap: "6px", flexWrap: "wrap" },
        badge: s.badge
    };
});

/** Rule name / match type / authorized recipients — shared by the Send panel and file details. */
function FarRuleDetails({ rule, evaluation, settings, file, compact }){
    const classes = useStyles();
    const rowClass = classes.row+" "+(compact ? classes.compactRow : "");
    const showDetails = settings.show_rule_details !== false;
    const showAllowed = settings.show_authorized_recipients !== false;
    // Recipients this rule excludes for this file (unresolved), with the matching entry.
    const excluded = {};
    ((file && file.pairs) || []).forEach(pair => {
        if(pair.ruleId === rule.id && pair.excluded && !pair.exception){
            excluded[pair.recipient] = pair;
        }
    });
    const excludedList = Object.keys(excluded);
    const unauthorized = (file ? file.unauthorizedRecipients || [] : []).filter(r => !excluded[r]);
    const action = rule.exclusionAction || FAR_EXCLUSION.BLOCK;
    return (
        <div className={classes.box}>
            <div className={rowClass}><span className={classes.key}>Rule name</span><span className={classes.value}>{rule.name}</span></div>
            {
                showDetails ?
                    <div className={rowClass}>
                        <span className={classes.key}>Match type</span>
                        <span className={classes.value}>
                            { rule.prefix ? <span>Prefix: {rule.prefix}{ rule.suffix || rule.sendlinxFolder ? " ("+getOpLabel(rule.prefixOp)+")" : "" }</span> : null }
                            { rule.suffix ? <span>Suffix: {rule.suffix}{ rule.sendlinxFolder ? " ("+getOpLabel(rule.suffixOp)+")" : "" }</span> : null }
                            { rule.sendlinxFolder ? <span>Sendlinx folder: {rule.sendlinxFolder} <em className={classes.note}>(and its subfolders)</em></span> : null }
                            { rule.directory ? <span>Local folder: {rule.directory} <em className={classes.note}>(checked by the Outlook plug-in only)</em></span> : null }
                        </span>
                    </div>
                : null
            }
            {
                showAllowed ?
                    <div className={rowClass}>
                        <span className={classes.key}>Authorized recipients</span>
                        <span className={classes.value}>{ rule.emails.map(email => <span key={email}>{email}</span>) }</span>
                    </div>
                : null
            }
            {
                showDetails && ruleHasExclusions(rule) ?
                    <div className={rowClass}>
                        <span className={classes.key}>Blocked recipients</span>
                        <span className={classes.value}>
                            {
                                rule.blockedListsVisible ?
                                    rule.blockedEmails.concat(rule.blockedDomains).map(entry => <span key={entry} className={classes.bad}>{entry}</span>)
                                : <span>{describeExclusionCounts(rule)}</span>
                            }
                            <span className={classes.note}>{FAR_EXCLUSION_LABEL[action]}. Blocked recipients take precedence over authorized recipients.</span>
                        </span>
                    </div>
                : null
            }
            {
                excludedList.length > 0 ?
                    <div className={rowClass}>
                        <span className={classes.key}>Blocked by FAR exclusion</span>
                        <span className={classes.value+" "+classes.bad}>
                            {
                                excludedList.map(r => (
                                    <span key={r}>{r} <em className={classes.note}>({excluded[r].exclusionType === "domain" ? "blocked domain "+excluded[r].exclusionValue : "blocked address"})</em></span>
                                ))
                            }
                        </span>
                    </div>
                : null
            }
            {
                unauthorized.length > 0 ?
                    <div className={rowClass}>
                        <span className={classes.key}>Not authorized</span>
                        <span className={classes.value+" "+classes.bad}>{ unauthorized.map(r => <span key={r}>{r === FAR_LINK_RECIPIENT ? "Share link (anyone with the link)" : r}</span>) }</span>
                    </div>
                : null
            }
        </div>
    );
}

FarRuleDetails.defaultProps = { compact: false, file: null, evaluation: null };
FarRuleDetails.propTypes = {
    rule: PropTypes.object.isRequired,
    evaluation: PropTypes.object,
    settings: PropTypes.object.isRequired,
    file: PropTypes.object,
    compact: PropTypes.bool
};

export default FarRuleDetails;
